import { Link } from '@tanstack/react-router'
import {
  BookOpen,
  House,
  Layers,
  Route as RouteIcon,
  Settings,
  type LucideIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { usePrerenderHandoff } from '../lib/prerender.js'
import { Mark } from './Mark.js'
import { ThemeSwitch } from './ThemeSwitch.js'

type NavPath = '/' | '/journey' | '/review' | '/spaces' | '/settings'
type NavKey = 'today' | 'journey' | 'review' | 'spaces' | 'settings'

interface NavItem {
  to: NavPath
  key: NavKey
  icon: LucideIcon
}

/** The four destinations of the bottom navigation on phones; settings live in the header. */
const PRIMARY: readonly NavItem[] = [
  { to: '/', key: 'today', icon: House },
  { to: '/journey', key: 'journey', icon: RouteIcon },
  { to: '/review', key: 'review', icon: Layers },
  { to: '/spaces', key: 'spaces', icon: BookOpen },
]
const SETTINGS: NavItem = { to: '/settings', key: 'settings', icon: Settings }

/**
 * Layout for every page: left rail from 1024 px, header and bottom navigation below. A skip
 * link is the first focusable element.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const { t } = useTranslation()
  // A signed-in visitor of /sign-in is redirected into the shell: the static sign-in page goes.
  usePrerenderHandoff()
  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[248px_minmax(0,1fr)]">
      <a
        href="#content"
        onClick={event => {
          // The router handles hash changes itself, so move focus explicitly.
          event.preventDefault()
          document.getElementById('content')?.focus()
        }}
        className="sr-only z-50 rounded-[10px] bg-raised px-4 py-3 font-semibold text-on-raised focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        {t('shell.skipToContent')}
      </a>

      <aside className="sticky top-0 hidden h-dvh flex-col gap-8 border-r border-line bg-surface px-4 py-6 lg:flex">
        <HomeLink />
        <nav aria-label={t('nav.main')} className="flex flex-col gap-1">
          {[...PRIMARY, SETTINGS].map(item => (
            <RailLink key={item.key} item={item} />
          ))}
        </nav>
        <ThemeSwitch className="mt-auto" />
      </aside>

      <header className="flex items-center justify-between px-5 pt-5 lg:hidden">
        <HomeLink />
        <Link
          to={SETTINGS.to}
          aria-label={t('nav.settings')}
          className="flex size-11 items-center justify-center rounded-full border border-line bg-surface text-text data-[status=active]:bg-raised data-[status=active]:text-on-raised"
        >
          <Settings size={20} aria-hidden="true" />
        </Link>
      </header>

      <main id="content" tabIndex={-1} className="px-5 pt-6 pb-32 outline-none lg:px-10 lg:py-10">
        <div className="mx-auto w-full max-w-5xl">{children}</div>
      </main>

      <nav
        aria-label={t('nav.main')}
        className="fixed inset-x-4 bottom-[max(1rem,env(safe-area-inset-bottom))] grid grid-cols-4 gap-1 rounded-[22px] border border-line bg-surface p-2 lg:hidden"
      >
        {PRIMARY.map(item => (
          <BottomLink key={item.key} item={item} />
        ))}
      </nav>
    </div>
  )
}

function HomeLink() {
  const { t } = useTranslation()
  return (
    <Link
      to="/"
      aria-label={t('shell.home')}
      className="flex items-center gap-2.5 rounded-[10px] text-text no-underline"
    >
      <Mark />
      <span className="font-display text-xl font-bold tracking-tight">{t('app.name')}</span>
    </Link>
  )
}

function RailLink({ item }: { item: NavItem }) {
  const { t } = useTranslation()
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' }}
      className="flex min-h-11 items-center gap-3 rounded-[12px] px-3 font-semibold text-muted no-underline hover:bg-bg hover:text-text data-[status=active]:bg-raised data-[status=active]:text-on-raised"
    >
      <Icon size={20} aria-hidden="true" />
      {t(`nav.${item.key}`)}
    </Link>
  )
}

function BottomLink({ item }: { item: NavItem }) {
  const { t } = useTranslation()
  const Icon = item.icon
  return (
    <Link
      to={item.to}
      activeOptions={{ exact: item.to === '/' }}
      className="flex min-h-14 flex-col items-center justify-center gap-1 rounded-[14px] text-xs font-semibold text-muted no-underline data-[status=active]:bg-raised data-[status=active]:text-on-raised"
    >
      <Icon size={22} aria-hidden="true" />
      {t(`nav.${item.key}`)}
    </Link>
  )
}
